import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Box, Button, Chip, FormControlLabel, IconButton, Menu, MenuItem, Radio, RadioGroup, Stack, TextField, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions, Typography } from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { useNavigate } from "react-router-dom";
import StyledDataGrid from "../../../components/StyledDataGrid";
import { useSnackbar } from "../../../components/snackbar/SnackbarProvider";
import { BASE_CHIP_SX, STATUS_CHIP_STYLES } from "../../../constants/statusColors";
import {
  fetchFollowUpActionItems,
  sendFollowUpEmail,
  fetchPatientContactById,
  confirmFollowUpAddress,
  updateFollowUpFulfillment,
  markFollowUpMissed,
  ensureFollowUpEncounter
} from "../../../utils/api";
import {
  formatShortDateKey,
  todayChicagoDateKey,
} from "../../../utils/appDateTime";
import { getDateKeyInTimeZone } from "../../../utils/dateFilters";
import { BIOBANK_ROUTES, BIOBANK_SUB_ROUTES } from "../../../constants/routes";
import FollowUpFilters from "./FollowUpFilters";
import { patientIdOf } from "../email-center/EmailPatientPicker";

const formatDateOnly = (value) => {
  if (!value) return "N/A";
  return formatShortDateKey(value, String(value));
};

const normalizeStatus = (value, fallback = "") =>
  String(value || fallback || "").trim().toUpperCase();

const hasValue = (value) => value !== undefined && value !== null && String(value).trim() !== "";

const getDueInfo = (row) => {
  const status = normalizeStatus(row.followUpStatus || row.status);
  if (status === "COMPLETED") {
    return { label: "Completed", style: STATUS_CHIP_STYLES.success, title: `Due Date: ${formatDateOnly(row.dueDate)}` };
  }
  if (status === "MISSED") {
    return { label: "Missed", style: STATUS_CHIP_STYLES.error, title: `Due Date: ${formatDateOnly(row.dueDate)}` };
  }

  if (row.daysOverdue != null && row.daysOverdue > 0) {
    return {
      label: `Overdue ${row.daysOverdue}d`,
      style: row.daysOverdue > 30 ? STATUS_CHIP_STYLES.error : STATUS_CHIP_STYLES.warning,
      title: `Due Date: ${formatDateOnly(row.dueDate)}`,
    };
  }
  if (row.daysUntilDue != null && row.daysUntilDue > 0) {
    return {
      label: `Due in ${row.daysUntilDue}d`,
      style: STATUS_CHIP_STYLES.default,
      title: `Due Date: ${formatDateOnly(row.dueDate)}`,
    };
  }
  if (row.daysOverdue === 0 || row.daysUntilDue === 0) {
    return { label: "Due today", style: STATUS_CHIP_STYLES.warning, title: `Due Date: ${formatDateOnly(row.dueDate)}` };
  }

  return { label: "N/A", style: STATUS_CHIP_STYLES.default, title: `Due Date: ${formatDateOnly(row.dueDate)}` };
};

const getMailOutDays = (row) => {
  if (row.daysSinceMailOut != null) return row.daysSinceMailOut;
  if (!row.mitraBoxOutboundDateTime) return null;
  const mailedDate = getDateKeyInTimeZone(row.mitraBoxOutboundDateTime);
  const today = todayChicagoDateKey();
  if (!mailedDate || !today) return null;
  const diff = new Date(`${today}T00:00:00Z`) - new Date(`${mailedDate}T00:00:00Z`);
  return Math.max(0, Math.floor(diff / 86400000));
};

const canEditContactInfo = (row) => normalizeStatus(row.fulfillmentStatus, "NONE") === "NONE";

const canChooseCollectionMethod = (row) => normalizeStatus(row.fulfillmentStatus, "NONE") === "NONE";

const EMAIL_TEMPLATE_OPTIONS = [
  {
    value: "DEFAULT",
    specCode: "FOLLOW_UP_ADDRESS_CONFIRMATION",
    label: "Address Confirmation",
  },
  {
    value: "SHIP_BACK_REMINDER",
    specCode: "FOLLOW_UP_SHIP_BACK_REMINDER",
    label: "Ship Back Reminder",
  },
];

const LANGUAGE_OPTIONS = [
  { value: "en", label: "English" },
  { value: "es", label: "Spanish" },
];

const getEmailTemplateOption = (value) => {
  return EMAIL_TEMPLATE_OPTIONS.find((option) => option.value === value)
    || EMAIL_TEMPLATE_OPTIONS[0];
};

const getPreferredLanguage = (row) => {
  const raw = String(row?.preferredLanguage || row?.language || "").toLowerCase();
  return raw.startsWith("es") ? "es" : "en";
};

const canOpenSampleCollection = (row) => {
  const fulfillmentStatus = normalizeStatus(row.fulfillmentStatus, "NONE");
  return fulfillmentStatus === "IN_CLINIC"
    || fulfillmentStatus === "MITRABOX_RETURNED"
    || hasValue(row.shipBackDate);
};

const getProgressParts = (row) => {
  const contactStatus = normalizeStatus(row.contactStatus, "NOT_CONTACTED");
  const addressStatus = normalizeStatus(row.addressStatus, "UNKNOWN");
  const fulfillmentStatus = normalizeStatus(row.fulfillmentStatus, "NONE");

  let contact = "Email —";
  if (contactStatus === "EMAIL_SENT") contact = "Email ✓";
  if (contactStatus === "SMS_SENT") contact = "SMS ✓";
  if (contactStatus === "CALL_DONE") contact = "Call ✓";
  if (contactStatus === "CONTACT_FAILED") contact = "Contact !";
  if (contactStatus === "CONFIRMED") contact = "Contact ✓";

  let address = "Address —";
  if (addressStatus === "CONFIRMED") address = "Address ✓";

  let fulfillment = "Kit —";
  if (fulfillmentStatus === "IN_CLINIC") fulfillment = "In-clinic";
  if (fulfillmentStatus === "MITRABOX_MAILED") {
    const daysSinceMailOut = getMailOutDays(row);
    fulfillment = daysSinceMailOut != null ? `Mailed ${daysSinceMailOut}d` : "Mailed";
  }
  if (fulfillmentStatus === "MITRABOX_RETURNED" || hasValue(row.shipBackDate)) {
    fulfillment = "Received ✓";
  }

  return [contact, address, fulfillment];
};

const getNextActionInfo = (row) => {
  const followUpStatus = normalizeStatus(row.followUpStatus || row.status);
  const contactStatus = normalizeStatus(row.contactStatus, "NOT_CONTACTED");
  const fulfillmentStatus = normalizeStatus(row.fulfillmentStatus, "NONE");

  if (followUpStatus === "COMPLETED") {
    return { label: "Completed", primary: "details" };
  }
  if (followUpStatus === "MISSED") {
    return { label: "Missed", primary: "details" };
  }
  if (fulfillmentStatus === "MITRABOX_MAILED" && !hasValue(row.shipBackDate)) {
    return { label: "Record Ship Back", primary: "received" };
  }
  if (row.daysOverdue != null && row.daysOverdue > 30) {
    return { label: "Mark Missed", primary: "missed", tone: "error" };
  }
  if (contactStatus === "NOT_CONTACTED") {
    return { label: "Send Email", primary: "email" };
  }
  if (fulfillmentStatus === "NONE") {
    return { label: "Choose Method", primary: "method" };
  }
  if (fulfillmentStatus === "IN_CLINIC") {
    return { label: "Open Collection", primary: "collection" };
  }
  if (hasValue(row.shipBackDate) || fulfillmentStatus === "MITRABOX_RETURNED") {
    return { label: "Open Collection", primary: "collection" };
  }

  return { label: "View details", primary: "details" };
};

const ActionItemsTab = ({ setError }) => {
  const navigate = useNavigate();
  const { openSnackbar } = useSnackbar();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalRows, setTotalRows] = useState(0);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [selectedBatchId, setSelectedBatchId] = useState("");
  
  // Dialog states
  const [activeDialog, setActiveDialog] = useState(null); // 'contact', 'method', 'received', 'missed'
  const [selectedRow, setSelectedRow] = useState(null);
  const [dialogData, setDialogData] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const selectedPatientId = patientIdOf(selectedPatient);

  const loadActions = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: paginationModel.page,
        size: paginationModel.pageSize,
      };
      if (selectedPatientId) params.patientId = selectedPatientId;
      if (selectedBatchId) params.batchId = selectedBatchId;

      const data = await fetchFollowUpActionItems(params);
      const content = Array.isArray(data?.content) ? data.content : Array.isArray(data) ? data : [];
      setRows(content.map((row, index) => ({
          ...row,
          id: row.followUpId || index,
          patientName: row.patientName || `${row.firstName || ''} ${row.lastName || ''}`.trim() || 'N/A',
          accountNumber: row.patient?.patientAccountNumber || row.patientAccountNumber || 'N/A',
          barcode: row.collectionBatchId || 'N/A',
          milestone: row.milestoneNumber ? `H${row.milestoneNumber}` : 'N/A',
          dueDate: row.scheduledDueDate,
          status: row.followUpStatus,
          contactStatus: row.contactStatus || 'NOT_CONTACTED',
          addressStatus: row.addressStatus || 'UNKNOWN',
          fulfillmentStatus: row.fulfillmentStatus || 'NONE'
        })));
      setTotalRows(data?.totalElements || content.length);
    } catch (err) {
      console.error("Failed to load action items:", err);
      setError("Failed to load action items.");
    } finally {
      setLoading(false);
    }
  }, [paginationModel.page, paginationModel.pageSize, selectedBatchId, selectedPatientId, setError]);

  useEffect(() => {
    loadActions();
  }, [loadActions]);

  const applyBatchFilter = useCallback((nextBatchId) => {
    setSelectedBatchId(nextBatchId || "");
    setPaginationModel((current) => ({ ...current, page: 0 }));
  }, []);

  const applyPatientFilter = useCallback((patient) => {
    setSelectedPatient(patient || null);
    setPaginationModel((current) => ({ ...current, page: 0 }));
  }, []);

  const clearFilters = useCallback(() => {
    setSelectedBatchId("");
    setSelectedPatient(null);
    setPaginationModel((current) => ({ ...current, page: 0 }));
  }, []);

  const handleAction = async (actionFn, ...args) => {
    setSubmitting(true);
    try {
      await actionFn(...args);
      setActiveDialog(null);
      await loadActions();
    } catch (err) {
      console.error("Action failed:", err);
      setError("Action failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const openDialog = (type, row, extraData = {}) => {
    setSelectedRow(row);
    if (type === 'email') {
      setDialogData({
        template: extraData.template || 'DEFAULT',
        language: getPreferredLanguage(row),
      });
    } else if (type === 'received') {
      setDialogData({ shipBackDate: todayChicagoDateKey(), mitraBoxReturnTrackingNumber: '' });
    } else if (type === 'missed') {
      setDialogData({ removedReason: '' });
    }
    setActiveDialog(type);
  };

  const handleViewEmails = (row) => {
    if (!row?.patientId) {
      setError("Cannot open Email Center because this row does not include a participant ID.");
      return;
    }

    navigate(`${BIOBANK_ROUTES.EMAIL_CENTER}?patientId=${encodeURIComponent(row.patientId)}`);
  };

  const handleSendEmail = async () => {
    if (!selectedRow?.followUpId) return;

    const templateOption = getEmailTemplateOption(dialogData.template);
    setSubmitting(true);
    setError("");
    try {
      await sendFollowUpEmail(selectedRow.followUpId, {
        specCode: templateOption.specCode,
        language: dialogData.language || "en",
      });
      setActiveDialog(null);
      openSnackbar(`Email sent to ${selectedRow.patientName}.`, "success");
      await loadActions();
    } catch (err) {
      console.error("Failed to send follow-up email:", err);
      setError(err?.message || "Failed to send email. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const contactDefaultsFromRow = (row, contactInfo = {}) => {
    const fulfillmentStatus = normalizeStatus(row.fulfillmentStatus, "NONE");
    const useSnapshot = fulfillmentStatus === "MITRABOX_MAILED" || fulfillmentStatus === "MITRABOX_RETURNED";

    return {
      email: contactInfo.email || row.email || '',
      mobilePhoneNumber: contactInfo.mobilePhoneNumber || row.mobilePhoneNumber || '',
      homeAddressLine1: useSnapshot ? (row.mailAddressLine1Snapshot || '') : (contactInfo.homeAddressLine1 || row.homeAddressLine1 || ''),
      homeAddressLine2: useSnapshot ? (row.mailAddressLine2Snapshot || '') : (contactInfo.homeAddressLine2 || row.homeAddressLine2 || ''),
      city: useSnapshot ? (row.mailCitySnapshot || '') : (contactInfo.city || row.city || ''),
      state: useSnapshot ? (row.mailStateSnapshot || '') : (contactInfo.state || row.state || ''),
      zipCode: useSnapshot ? (row.mailZipCodeSnapshot || '') : (contactInfo.zipCode || row.zipCode || '')
    };
  };

  const openContactDialog = async (row) => {
    setSelectedRow(row);
    try {
      const response = await fetchPatientContactById(row.patientId);
      const contactInfo = response.data || response || {};
      setDialogData(contactDefaultsFromRow(row, contactInfo));
      setActiveDialog('contact');
    } catch (error) {
      console.error("Failed to fetch contact info:", error);
      setDialogData(contactDefaultsFromRow(row));
      setActiveDialog('contact');
    }
  };

  const openMethodDialog = async (row) => {
    setSelectedRow(row);
    const baseData = {
      collectionMethod: "",
      mitraBoxOutboundDateTime: todayChicagoDateKey(),
      mitraBoxOutboundTrackingNumber: '',
    };

    try {
      const response = await fetchPatientContactById(row.patientId);
      const contactInfo = response.data || response || {};
      setDialogData({
        ...baseData,
        ...contactDefaultsFromRow(row, contactInfo),
      });
    } catch (error) {
      console.error("Failed to fetch contact info:", error);
      setDialogData({
        ...baseData,
        ...contactDefaultsFromRow(row),
      });
    }
    setActiveDialog("method");
  };

  const isMailMethodReady = () => {
    if (dialogData.collectionMethod !== "MAIL") return true;
    return hasValue(dialogData.homeAddressLine1)
      && hasValue(dialogData.city)
      && hasValue(dialogData.state)
      && hasValue(dialogData.zipCode)
      && hasValue(dialogData.mitraBoxOutboundDateTime);
  };

  const handleCollectionMethodConfirm = async () => {
    if (!selectedRow || !dialogData.collectionMethod) return;
    setSubmitting(true);
    try {
      if (dialogData.collectionMethod === "IN_CLINIC") {
        await updateFollowUpFulfillment(selectedRow.followUpId, {
          fulfillmentStatus: "IN_CLINIC",
        });
      } else {
        const contactPayload = {
          email: dialogData.email,
          mobilePhoneNumber: dialogData.mobilePhoneNumber,
          homeAddressLine1: dialogData.homeAddressLine1,
          homeAddressLine2: dialogData.homeAddressLine2,
          city: dialogData.city,
          state: dialogData.state,
          zipCode: dialogData.zipCode,
        };
        await confirmFollowUpAddress(selectedRow.followUpId, contactPayload);
        await updateFollowUpFulfillment(selectedRow.followUpId, {
          fulfillmentStatus: "MITRABOX_MAILED",
          mitraBoxOutboundDateTime: dialogData.mitraBoxOutboundDateTime,
          mitraBoxOutboundTrackingNumber: dialogData.mitraBoxOutboundTrackingNumber,
        });
      }
      setActiveDialog(null);
      await loadActions();
    } catch (err) {
      console.error("Failed to choose collection method:", err);
      setError("Failed to update collection method. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleOpenCollection = async (row) => {
    if (!canOpenSampleCollection(row)) {
      setError("Sample collection is available after in-clinic is selected or the Mitrabox has been received.");
      return;
    }
    try {
      setLoading(true);
      const res = row.encounterId ? row : await ensureFollowUpEncounter(row.followUpId);
      const encounterId = row.encounterId || res?.encounterId;
      if (encounterId) {
        navigate(BIOBANK_SUB_ROUTES.SAMPLE_COLLECTION_FOLLOWUP(encounterId), {
          state: {
            encounter: {
              id: encounterId,
              encounterId,
              followUpId: row.followUpId,
              displayFollowUpCode: row.displayFollowUpCode,
              followUpCode: row.displayFollowUpCode,
              rootSampleId: row.rootSampleId,
              patientId: row.patientId,
              checkInDateTime: row.checkInDateTime || res?.checkInDateTime
            },
            patient: {
              id: row.patientId,
              patientId: row.patientId,
              patientAccountNumber: row.accountNumber,
              accountNumber: row.accountNumber,
              firstName: row.patientName ? row.patientName.split(" ")[0] : row.firstName,
              lastName: row.patientName ? row.patientName.split(" ").slice(1).join(" ") : row.lastName
            },
            returnUrl: window.location.pathname + window.location.search
          }
        });
      } else {
        setError("Failed to create or retrieve encounter for sample collection.");
      }
    } catch (e) {
      setError("Error opening sample collection.");
    } finally {
      setLoading(false);
    }
  };

  const handlePrimaryAction = (row) => {
    const action = getNextActionInfo(row).primary;
    if (action === "email") {
      openDialog("email", row, { template: "DEFAULT" });
      return;
    }
    if (action === "reminder") {
      openDialog("email", row, { template: "SHIP_BACK_REMINDER" });
      return;
    }
    if (action === "address") {
      openContactDialog(row);
      return;
    }
    if (action === "method") {
      openMethodDialog(row);
      return;
    }
    if (action === "received") {
      openDialog("received", row);
      return;
    }
    if (action === "collection") {
      handleOpenCollection(row);
      return;
    }
    if (action === "missed") {
      openDialog("missed", row);
      return;
    }
  };

  const PrimaryActionButton = ({ row }) => {
    const actionInfo = getNextActionInfo(row);
    const actionLabels = {
      email: "Send Email",
      reminder: "Send Reminder",
      address: "Confirm Address",
      method: "Choose Method",
      received: "Record Ship Back",
      collection: "Open Collection",
      missed: "Mark Missed",
      details: "View Details",
    };
    const label = actionLabels[actionInfo.primary] || "View Details";
    const disabled = actionInfo.primary === "details";
    const color = actionInfo.primary === "missed" ? "error" : "primary";

    return (
      <Button
        size="small"
        variant={disabled ? "outlined" : "contained"}
        color={color}
        disabled={disabled || submitting || loading}
        onClick={() => handlePrimaryAction(row)}
        sx={{
          width: 152,
          textTransform: "uppercase",
          fontWeight: 700,
          borderRadius: "10px",
          boxShadow: disabled ? "none" : undefined,
        }}
      >
        {label}
      </Button>
    );
  };

  const ActionMenu = ({ row }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);
    const followUpStatus = normalizeStatus(row.followUpStatus || row.status);
    const fulfillmentStatus = normalizeStatus(row.fulfillmentStatus, "NONE");

    const MenuActionItem = ({ label, description, disabled = false, tone = "default", onClick }) => {
      return (
        <Tooltip
          title={description || ""}
          placement="left"
          disableHoverListener={!description}
          componentsProps={{
            tooltip: {
              sx: {
                maxWidth: "none",
                whiteSpace: "nowrap",
              },
            },
          }}
        >
          <Box component="span" sx={{ display: "block" }}>
            <MenuItem
              disabled={disabled}
              onClick={onClick}
              sx={{
                color: tone === "error" ? "#8A1C1C" : "text.primary",
                fontSize: "0.875rem",
                fontWeight: 400,
                minHeight: 32,
                px: 2,
                py: 0.75,
                whiteSpace: "nowrap",
              }}
            >
              {label}
            </MenuItem>
          </Box>
        </Tooltip>
      );
    };

    return (
      <Box>
        <IconButton
          size="small"
          onClick={(e) => setAnchorEl(e.currentTarget)}
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: "10px",
            color: "text.secondary",
            "&:hover": {
              bgcolor: "action.hover",
              color: "text.primary",
            },
          }}
        >
          <MoreVertIcon />
        </IconButton>
        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={() => setAnchorEl(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          PaperProps={{
            sx: {
              mt: 0.5,
              minWidth: 220,
              borderRadius: 0,
              border: "1px solid",
              borderColor: "#eaeaea",
              boxShadow: "0px 8px 24px rgba(0, 0, 0, 0.12)",
              py: 0,
            },
          }}
          MenuListProps={{
            sx: { py: 0 },
          }}
        >
          <MenuActionItem
            label="Send Email"
            description="Send address or ship-back reminder"
            onClick={() => { setAnchorEl(null); openDialog('email', row, { template: "DEFAULT" }); }}
          />
          <MenuActionItem
            label="View Emails"
            description="Open this participant in Email Center"
            onClick={() => { setAnchorEl(null); handleViewEmails(row); }}
          />
          <MenuActionItem
            label="Edit Contact Info"
            description="Available before collection method is chosen"
            disabled={!canEditContactInfo(row)}
            onClick={() => { setAnchorEl(null); openContactDialog(row); }}
          />
          <MenuActionItem
            label="Choose Collection Method"
            description="Mail Mitrabox or in-clinic collection"
            disabled={!canChooseCollectionMethod(row)}
            onClick={() => { setAnchorEl(null); openMethodDialog(row); }}
          />
          {fulfillmentStatus === "MITRABOX_MAILED" && !hasValue(row.shipBackDate) && (
            <MenuActionItem
              label="Record Ship Back"
              description="Lab received the mailed Mitrabox"
              tone="success"
              onClick={() => { setAnchorEl(null); openDialog('received', row); }}
            />
          )}
          {followUpStatus !== "COMPLETED" && followUpStatus !== "MISSED" && (
            <MenuActionItem
              label="Mark Missed"
              description="Skip this milestone with a reason"
              tone="error"
              onClick={() => { setAnchorEl(null); openDialog('missed', row); }}
            />
          )}
          <MenuActionItem
            label="Open Sample Collection"
            description="Available after in-clinic or ship back"
            disabled={!canOpenSampleCollection(row)}
            onClick={() => {
              setAnchorEl(null);
              handleOpenCollection(row);
            }}
          />
        </Menu>
      </Box>
    );
  };

  const columns = useMemo(() => [
    { field: "patientName", headerName: "Participant", flex: 1 },

    { field: "accountNumber", headerName: "MRN", flex: 0.5 },
    { field: "barcode", headerName: "Barcode", flex: 0.65 },

    { field: "milestone", headerName: "Phase", flex: 0.5 },
    {
      field: "dueDate",
      headerName: "Due",
      flex: 0.65,
      minWidth: 135,
      renderCell: (params) => {
        const row = params.row;
        const dueInfo = getDueInfo(row);
        
        return (
          <Chip
            label={dueInfo.label}
            size="small"
            sx={{
              ...BASE_CHIP_SX,
              ...dueInfo.style,
              minWidth: dueInfo.label.includes("Review") ? 132 : 90,
              textTransform: "none",
            }}
          />
        );
      },
    },
    {
      field: "progress",
      headerName: "Progress",
      flex: 1.8,
      minWidth: 300,
      renderCell: (params) => {
        const parts = getProgressParts(params.row);
        const getStyle = (text) => {
          if (text.includes("✓")) return STATUS_CHIP_STYLES.success;
          if (text.includes("?")) return STATUS_CHIP_STYLES.warning;
          if (text.includes("—")) return STATUS_CHIP_STYLES.default;
          if (text.includes("!")) return STATUS_CHIP_STYLES.error;
          if (text.includes("Mailed")) return STATUS_CHIP_STYLES.warning;
          if (text.includes("In-clinic")) return STATUS_CHIP_STYLES.success;
          return STATUS_CHIP_STYLES.default;
        };

        return (
          <Stack direction="row" spacing={0.75} alignItems="center" sx={{ flexWrap: "wrap", rowGap: 0.5 }}>
            {parts.map((part, idx) => (
              <Chip
                key={idx}
                label={part}
                size="small"
                sx={{
                  ...BASE_CHIP_SX,
                  ...getStyle(part),
                  textTransform: "none",
                }}
              />
            ))}
          </Stack>
        );
      }
    },
    {
      field: "nextAction",
      headerName: "Next Action",
      flex: 1.0,
      minWidth: 190,
      renderCell: (params) => <PrimaryActionButton row={params.row} />,
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 0.35,
      minWidth: 90,
      sortable: false,
      align: "center",
      renderCell: (params) => <ActionMenu row={params.row} />,
    },
  ], []);

  return (

    <Box sx={{ flex: 1, minHeight: 0, width: "100%", display: "flex", flexDirection: "column" }} data-tour-id="tour-followup-actions-table">
      <FollowUpFilters
        batchId={selectedBatchId}
        onBatchIdChange={applyBatchFilter}
        selectedPatient={selectedPatient}
        onSelectedPatientChange={applyPatientFilter}
        onClear={clearFilters}
      />

      <Box sx={{ flex: 1, minHeight: 0 }}>
        <StyledDataGrid
          rows={rows}
          columns={columns}
          loading={loading}
          pageSizeOptions={[10, 25, 50, 100]}
          paginationMode="server"
          rowCount={totalRows}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pagination
          getRowId={(r) => r.id}
          disableSelectionOnClick
        />
      </Box>

      {/* Dialogs */}
      <Dialog open={activeDialog === 'email'} onClose={() => !submitting && setActiveDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Send Email</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2">
              Send <strong>{getEmailTemplateOption(dialogData.template).label}</strong> to{" "}
              <strong>{selectedRow?.patientName}</strong>
              {selectedRow?.email ? ` <${selectedRow.email}>` : ""}.
            </Typography>
            <TextField
              select
              label="Email Template"
              size="small"
              fullWidth
              value={dialogData.template}
              onChange={(e) => setDialogData({ ...dialogData, template: e.target.value })}
            >
              {EMAIL_TEMPLATE_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Language"
              size="small"
              fullWidth
              value={dialogData.language || "en"}
              onChange={(e) => setDialogData({ ...dialogData, language: e.target.value })}
            >
              {LANGUAGE_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setActiveDialog(null)} disabled={submitting}>Cancel</Button>
          <Button
            onClick={handleSendEmail}
            variant="contained"
            disabled={submitting || !selectedRow?.followUpId}
          >
            Send
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={activeDialog === 'contact'} onClose={() => !submitting && setActiveDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Edit Contact Info</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Email" size="small" fullWidth value={dialogData.email} onChange={(e) => setDialogData({...dialogData, email: e.target.value})} />
            <TextField label="Phone" size="small" fullWidth value={dialogData.mobilePhoneNumber} onChange={(e) => setDialogData({...dialogData, mobilePhoneNumber: e.target.value})} />
            <TextField label="Address Line 1" size="small" fullWidth value={dialogData.homeAddressLine1} onChange={(e) => setDialogData({...dialogData, homeAddressLine1: e.target.value})} />
            <TextField label="Address Line 2" size="small" fullWidth value={dialogData.homeAddressLine2} onChange={(e) => setDialogData({...dialogData, homeAddressLine2: e.target.value})} />
            <Stack direction="row" spacing={1}>
              <TextField label="City" size="small" value={dialogData.city} onChange={(e) => setDialogData({...dialogData, city: e.target.value})} />
              <TextField label="State" size="small" value={dialogData.state} onChange={(e) => setDialogData({...dialogData, state: e.target.value})} />
            </Stack>
            <TextField label="Zip Code" size="small" fullWidth value={dialogData.zipCode} onChange={(e) => setDialogData({...dialogData, zipCode: e.target.value})} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setActiveDialog(null)} disabled={submitting}>Cancel</Button>
          <Button onClick={() => handleAction(confirmFollowUpAddress, selectedRow?.followUpId, dialogData)} variant="contained" disabled={submitting}>Save</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={activeDialog === 'method'} onClose={() => !submitting && setActiveDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Choose Collection Method</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2">
              Choose how <strong>{selectedRow?.patientName}</strong> will complete this follow-up sample.
            </Typography>
            <RadioGroup
              value={dialogData.collectionMethod || ""}
              onChange={(e) => setDialogData({ ...dialogData, collectionMethod: e.target.value })}
            >
              <FormControlLabel
                value="MAIL"
                control={<Radio />}
                label="Mail Mitrabox to participant"
              />
              <FormControlLabel
                value="IN_CLINIC"
                control={<Radio />}
                label="In-clinic collection"
              />
            </RadioGroup>

            {dialogData.collectionMethod === "MAIL" && (
              <Stack spacing={2}>
                <Alert severity="info">
                  Mail collection requires a confirmed mailing address and mail-out date.
                </Alert>
                <TextField label="Email" size="small" fullWidth value={dialogData.email || ""} onChange={(e) => setDialogData({...dialogData, email: e.target.value})} />
                <TextField label="Phone" size="small" fullWidth value={dialogData.mobilePhoneNumber || ""} onChange={(e) => setDialogData({...dialogData, mobilePhoneNumber: e.target.value})} />
                <TextField required label="Address Line 1" size="small" fullWidth value={dialogData.homeAddressLine1 || ""} onChange={(e) => setDialogData({...dialogData, homeAddressLine1: e.target.value})} />
                <TextField label="Address Line 2" size="small" fullWidth value={dialogData.homeAddressLine2 || ""} onChange={(e) => setDialogData({...dialogData, homeAddressLine2: e.target.value})} />
                <Stack direction="row" spacing={1}>
                  <TextField required label="City" size="small" value={dialogData.city || ""} onChange={(e) => setDialogData({...dialogData, city: e.target.value})} />
                  <TextField required label="State" size="small" value={dialogData.state || ""} onChange={(e) => setDialogData({...dialogData, state: e.target.value})} />
                </Stack>
                <TextField required label="Zip Code" size="small" fullWidth value={dialogData.zipCode || ""} onChange={(e) => setDialogData({...dialogData, zipCode: e.target.value})} />
                <TextField required label="Mail-out Date" type="date" size="small" fullWidth value={dialogData.mitraBoxOutboundDateTime || ""} onChange={(e) => setDialogData({...dialogData, mitraBoxOutboundDateTime: e.target.value})} InputLabelProps={{ shrink: true }} />
                <TextField label="Tracking Number (Optional)" size="small" fullWidth value={dialogData.mitraBoxOutboundTrackingNumber || ""} onChange={(e) => setDialogData({...dialogData, mitraBoxOutboundTrackingNumber: e.target.value})} />
              </Stack>
            )}

            {dialogData.collectionMethod === "IN_CLINIC" && (
              <Alert severity="info">
                In-clinic collection does not require mailing address confirmation. The sample can be collected through the follow-up encounter.
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setActiveDialog(null)} disabled={submitting}>Cancel</Button>
          <Button
            onClick={handleCollectionMethodConfirm}
            variant="contained"
            disabled={submitting || !dialogData.collectionMethod || !isMailMethodReady()}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={activeDialog === 'received'} onClose={() => !submitting && setActiveDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Record Ship Back</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Ship Back Date (Lab Received)" type="date" size="small" fullWidth value={dialogData.shipBackDate} onChange={(e) => setDialogData({...dialogData, shipBackDate: e.target.value})} InputLabelProps={{ shrink: true }} />
            <TextField label="Return Tracking Number (Optional)" size="small" fullWidth value={dialogData.mitraBoxReturnTrackingNumber} onChange={(e) => setDialogData({...dialogData, mitraBoxReturnTrackingNumber: e.target.value})} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setActiveDialog(null)} disabled={submitting}>Cancel</Button>
          <Button onClick={() => handleAction(updateFollowUpFulfillment, selectedRow.followUpId, { fulfillmentStatus: "MITRABOX_RETURNED", ...dialogData })} variant="contained" disabled={submitting || !dialogData.shipBackDate}>Confirm</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={activeDialog === 'missed'} onClose={() => !submitting && setActiveDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Mark Missed</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Alert severity={selectedRow?.daysOverdue > 30 ? "warning" : "info"}>
              {selectedRow?.daysOverdue > 30
                ? "This follow-up is more than 30 days overdue. Marking it missed will skip this milestone and activate the next planned milestone."
                : "This follow-up is not more than 30 days overdue yet. Please confirm you still want to mark it missed."}
            </Alert>
            <TextField label="Reason for Missed Milestone" size="small" fullWidth multiline rows={3} value={dialogData.removedReason} onChange={(e) => setDialogData({...dialogData, removedReason: e.target.value})} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setActiveDialog(null)} disabled={submitting}>Cancel</Button>
          <Button onClick={() => handleAction(markFollowUpMissed, selectedRow.followUpId, dialogData)} color="error" variant="contained" disabled={submitting || !dialogData.removedReason}>Mark Missed</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ActionItemsTab;
